import { useQuery } from '@tanstack/react-query';
import type { UIMessage } from 'ai';
import { useEffect, useRef } from 'react';

import { CHAT_SERVER_URL } from '@/config';
import { useGlobalStore } from '@/store';

type ChatOpeningResponse = {
    reply: string;
    history: unknown;
};

const OPENING_MESSAGE_ID = 'chat-opening';

const fetchChatOpening = async (wavefleetToken: string): Promise<ChatOpeningResponse> => {
    const response = await fetch(`${CHAT_SERVER_URL}/chat/opening`, {
        method: 'POST',
        headers: { 'X-Wavefleet-Token': wavefleetToken },
    });
    return response.json() as Promise<ChatOpeningResponse>;
};

type UseChatOpenerParams = {
    wavefleetToken: string | undefined;
    isOpen: boolean;
    setMessages: (messages: UIMessage[]) => void;
};

type UseChatOpenerResult = {
    isFetchingOpeningReply: boolean;
    consumeOpeningHistoryOptions: () => { body: { history: unknown } } | undefined;
    resetOpeningHistory: () => void;
};

export const useChatOpener = ({ wavefleetToken, isOpen, setMessages }: UseChatOpenerParams): UseChatOpenerResult => {
    const setHasUnreadChatMessage = useGlobalStore((state) => state.setHasUnreadChatMessage);
    const historyRef = useRef<unknown>(null);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    const hasOpenedRef = useRef(false);

    const { data, isFetching } = useQuery({
        queryKey: ['chat-opening', wavefleetToken],
        queryFn: () => {
            if (!wavefleetToken) {
                return Promise.reject(new Error('useChatOpener: missing wavefleet token'));
            }
            return fetchChatOpening(wavefleetToken);
        },
        enabled: Boolean(wavefleetToken) && !hasOpenedRef.current,
    });

    useEffect(() => {
        if (!data) {
            return;
        }

        hasOpenedRef.current = true;
        historyRef.current = data.history;
        setMessages([{ id: OPENING_MESSAGE_ID, role: 'assistant', parts: [{ type: 'text', text: data.reply }] }]);
        if (!isOpenRef.current) {
            setHasUnreadChatMessage(true);
        }
    }, [data, setMessages, setHasUnreadChatMessage]);

    const consumeOpeningHistoryOptions = (): { body: { history: unknown } } | undefined => {
        if (historyRef.current === null) {
            return undefined;
        }

        const history = historyRef.current;
        historyRef.current = null;
        return { body: { history } };
    };

    const resetOpeningHistory = (): void => {
        historyRef.current = null;
    };

    return { isFetchingOpeningReply: isFetching, consumeOpeningHistoryOptions, resetOpeningHistory };
};
